/**
 * EntryProofPage.jsx
 * Route: /entry-proof/:submissionId?token=<edit_token>
 *
 * Public, no-login "proof of what I signed" page for a single entry
 * submission — reachable from the link in the entrant's confirmation
 * email, and also the page the backend's headless-browser PDF renderer
 * (pdf_utils.render_submission_proof_pdf) navigates to for both the
 * emailed PDF attachment and the secretary's "Download PDF" button. Gated
 * by the submission's own edit_token via GET /api/entries/<id>/proof, not
 * a session.
 *
 * Sets data-proof-ready="true" once loaded so the PDF renderer has a
 * concrete signal to wait on, rather than guessing from network idleness
 * (this page's own data fetch is what "ready" actually means here).
 */
import { useEffect, useState } from 'react';
import '../Secretary.css';
import '../EntryForm.css';
import EntrySubmissionView from './EntrySubmissionView';

const API_KEY = import.meta.env.VITE_API_KEY || '';

export default function EntryProofPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const submissionId = window.location.pathname.split('/').filter(Boolean).pop();
  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    if (!submissionId || isNaN(submissionId) || !token) {
      setError('This link is missing information and can\'t be used.');
      return;
    }
    fetch(`/api/entries/${submissionId}/proof?token=${encodeURIComponent(token)}`, {
      headers: { 'X-API-Key': API_KEY },
    })
      .then(res => res.ok ? res.json() : res.json().then(d => Promise.reject(new Error(d.error || `HTTP ${res.status}`))))
      .then(setData)
      .catch(err => setError(err.message));
  }, [submissionId, token]);

  return (
    <div className="secretary-portal App entry-form-page" data-proof-ready={data || error ? 'true' : undefined}
         style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem' }}>
      {error && (
        <div className="sec-section" style={{ borderLeft: '4px solid var(--danger-color)' }}>
          <h3 style={{ marginTop: 0 }}>Couldn&apos;t load this entry</h3>
          <p>{error}</p>
        </div>
      )}
      {!data && !error && <p>Loading…</p>}
      {data && <EntrySubmissionView event={data.event} submission={data.submission} />}
    </div>
  );
}
